// src/pages/ServiceTasksPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  SelectChangeEvent,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { UserRole } from '../types/chat';
import { api } from '../services/api';
import WhatsappTaskThread from '../components/WhatsappTaskThread';
import ChatTaskThread from '../components/ChatTaskThread';
import { useTranslation } from 'react-i18next';

interface ServiceTaskConfig {
  Departments?: Array<{
    ID: string;
    Code: string;
    Description: string;
    DisplayOrder?: number;
    Inactive?: boolean;
  }>;
  Groups?: Array<{
    ID: string;
    Code: string;
    Description: string;
    DisplayOrder?: number;
    Inactive?: boolean;
    Department?: {
      ID: string;
      Code: string;
      Description: string;
      DisplayOrder?: number;
      Inactive?: boolean;
    };
  }>;
  Types: Array<{
    ID: string;
    Code: string;
    Description: string;
    DisplayOrder?: number;
    Inactive?: boolean;
    DepartmentID?: string;
    GroupID?: string;
    Group?: {
      Department?: {
        ID: string;
        Code: string;
        Description: string;
        DisplayOrder?: number;
        Inactive?: boolean;
      };
      ID: string;
      Code: string;
      Description: string;
      DisplayOrder?: number;
      Inactive?: boolean;
    };
  }>;
}

interface Room {
  id: string;
  roomNumber: string;
  roomType?: string;
  floor?: string;
}

interface ServiceTask {
  id: string;
  taskTypeId?: string;
  localityType: 'Room' | 'FunctionSpace' | 'Location' | 'PublicPlace';
  localityId: string;
  priority: 'Normal' | 'Low' | 'High';
  dueOn?: string;
  notes?: string;
  statusLevelId?: string;
  originId?: string;
  waLineId?: string;
  waSessionId?: string;
  waLogId?: string;
  sourceChatId?: string;
  responsibleEmployeeId?: string;
  imageString?: string;
  hotelId: string;
  createdAt: string;
  updatedAt: string;
  source?: 'manual' | 'chat';
  status?: string;
  taskId?: string;
  room?: {
    id: string;
    roomNumber: string;
  };
  responsibleEmployee?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

const ServiceTasksPage: React.FC = () => {
  const { user } = useAuth();
  const { connection } = useSocket();
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [tasks, setTasks] = useState<ServiceTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<ServiceTask | null>(null);
  const [serviceTaskConfig, setServiceTaskConfig] = useState<ServiceTaskConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('');
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const hasDepartments = Boolean(serviceTaskConfig?.Departments?.length);
  const isChatTaskReadOnly = Boolean(editingTask?.source === 'chat');

  // Form state
  const [formData, setFormData] = useState({
    departmentId: '',
    taskTypeId: '',
    localityType: 'Room' as 'Room' | 'FunctionSpace' | 'Location' | 'PublicPlace',
    localityId: '',
    priority: 'Normal' as 'Normal' | 'Low' | 'High',
    dueOn: '',
    notes: '',
    imageString: '',
  });

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/service-tasks');
      setTasks(response.data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching service tasks:', err);
      setError(err.response?.data?.message || t('serviceTasks.errorFetching'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchServiceTaskConfig = useCallback(async () => {
    try {
      setConfigLoading(true);
      const response = await api.get('/gsc-api/service-tasks/config');
      console.log('Service task config loaded:', response.data);
      setServiceTaskConfig(response.data);
    } catch (err) {
      console.error('Error fetching service task config:', err);
      setError(t('serviceTasks.errorFetchingConfig'));
    } finally {
      setConfigLoading(false);
    }
  }, [t]);

  const fetchRooms = useCallback(async () => {
    try {
      const response = await api.get('/rooms');
      console.log('Rooms loaded:', response.data);
      setRooms(response.data);
    } catch (err) {
      console.error('Error fetching rooms:', err);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchServiceTaskConfig();
    fetchRooms();
  }, [fetchTasks, fetchServiceTaskConfig, fetchRooms]);

  // Live updates: refresh the list when a task is created elsewhere (e.g. WhatsApp bot)
  useEffect(() => {
    if (!connection) return;
    const handleTaskCreated = () => {
      fetchTasks();
    };
    connection.on('ServiceTaskCreated', handleTaskCreated);
    return () => {
      connection.off('ServiceTaskCreated', handleTaskCreated);
    };
  }, [connection, fetchTasks]);

  const handleOpenDialog = (task?: ServiceTask) => {
    if (task?.source === 'chat') {
      setError(t('serviceTasks.chatTaskReadOnly'));
      return;
    }

    if (task) {
      setEditingTask(task);
      
      // Find the task type to extract department and group
      let deptId = '';
      let groupId = '';
      if (task.taskTypeId && serviceTaskConfig?.Types) {
        const taskType = serviceTaskConfig.Types.find(t => t.ID === task.taskTypeId);
        if (taskType?.Group) {
          groupId = taskType.Group.ID;
          if (taskType.Group.Department) {
            deptId = taskType.Group.Department.ID;
          }
        }
      }
      
      setSelectedDepartmentId(deptId);
      setSelectedGroupId(groupId);
      setFormData({
        departmentId: deptId,
        taskTypeId: task.taskTypeId || '',
        localityType: task.localityType,
        localityId: task.localityId,
        priority: task.priority,
        dueOn: task.dueOn ? new Date(task.dueOn).toISOString().slice(0, 16) : '',
        notes: task.notes || '',
        imageString: task.imageString || '',
      });
      setImagePreview(task.imageString || null);
    } else {
      setEditingTask(null);
      setFormData({
        departmentId: '',
        taskTypeId: '',
        localityType: 'Room',
        localityId: '',
        priority: 'Normal',
        dueOn: '',
        notes: '',
        imageString: '',
      });
      setImagePreview(null);
      setSelectedDepartmentId('');
      setSelectedGroupId('');
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTask(null);
    setImagePreview(null);
    setSelectedDepartmentId('');
    setSelectedGroupId('');
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        dueOn: formData.dueOn ? new Date(formData.dueOn).toISOString() : undefined,
      };

      if (editingTask) {
        await api.put(`/service-tasks/${editingTask.id}`, payload);
      } else {
        await api.post('/service-tasks', payload);
      }

      handleCloseDialog();
      fetchTasks();
    } catch (err: any) {
      console.error('Error saving service task:', err);
      setError(err.response?.data?.message || t('serviceTasks.errorSaving'));
    }
  };

  const handleDelete = async (taskId: string) => {
    const targetTask = tasks.find((task) => task.id === taskId);
    if (targetTask?.source === 'chat') {
      setError(t('serviceTasks.chatTaskReadOnly'));
      return;
    }

    if (!window.confirm(t('serviceTasks.confirmDelete'))) {
      return;
    }

    try {
      await api.delete(`/service-tasks/${taskId}`);
      fetchTasks();
    } catch (err: any) {
      console.error('Error deleting service task:', err);
      setError(err.response?.data?.message || t('serviceTasks.errorDeleting'));
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High':
        return 'error';
      case 'Low':
        return 'success';
      default:
        return 'default';
    }
  };

const getStatusConfigs = (isDark: boolean) => ({
  pending: { 
    labelKey: 'serviceTasks.statusPending', 
    color: isDark ? '#fb923c' : '#f97316', 
    bg: isDark ? '#431407' : '#fff7ed' 
  },
  in_progress: { 
    labelKey: 'serviceTasks.statusInProgress', 
    color: isDark ? '#60a5fa' : '#2563eb', 
    bg: isDark ? '#1e3a8a' : '#eff6ff' 
  },
  approved: { 
    labelKey: 'serviceTasks.statusApproved', 
    color: isDark ? '#38bdf8' : '#0ea5e9', 
    bg: isDark ? '#0c4a6e' : '#e0f2fe' 
  },
  completed: { 
    labelKey: 'serviceTasks.statusDone', 
    color: isDark ? '#4ade80' : '#16a34a', 
    bg: isDark ? '#14532d' : '#ecfdf5' 
  },
  done: { 
    labelKey: 'serviceTasks.statusDone', 
    color: isDark ? '#4ade80' : '#16a34a', 
    bg: isDark ? '#14532d' : '#ecfdf5' 
  },
  cancelled: { 
    labelKey: 'serviceTasks.statusCancelled', 
    color: isDark ? '#f87171' : '#dc2626', 
    bg: isDark ? '#7f1d1d' : '#fef2f2' 
  },
});

const getStatusDisplay = (status: string | undefined, t: (key: string) => string, isDark: boolean) => {
  if (!status) {
    return {
      label: t('serviceTasks.statusUnknown'),
      color: isDark ? '#9ca3af' : '#6b7280',
      bg: isDark ? '#1f2937' : '#f3f4f6',
    };
  }

  const statusConfigs = getStatusConfigs(isDark);
  const config = statusConfigs[status as keyof typeof statusConfigs];
  if (!config) {
    return {
      label: status,
      color: isDark ? '#9ca3af' : '#6b7280',
      bg: isDark ? '#1f2937' : '#f3f4f6',
    };
  }

  return {
    label: t(config.labelKey),
    color: config.color,
    bg: config.bg,
  };
};

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setFormData({ ...formData, imageString: base64String });
        setImagePreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setFormData({ ...formData, imageString: '' });
    setImagePreview(null);
  };

  const canDelete = user?.role === UserRole.ADMIN || user?.role === UserRole.SUPER_ADMIN;

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        width: '100%',
        bgcolor: theme.palette.background.default,
        py: 4,
        px: 3,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
      }}
    >
      <Box
        sx={{
          maxWidth: 1000,
          width: '100%',
        }}
      >
        {/* Header */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2, 
            width: '100%',
          }}
        >
          <Box sx={{ flex: 1 }}>  {/* Başlık için alan */}
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 800,
                color: theme.palette.text.primary,
                mb: 0.5,
              }}
            >
              {t('serviceTasks.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.role === 'admin' 
                ? t('serviceTasks.manageAllTasks')
                : t('serviceTasks.manageYourTasks')
              }
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="medium"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              borderRadius: 2,
              px: 2,
              py: 1,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              boxShadow: isDark 
                ? '0 2px 8px rgba(30, 59, 138, 0.5)' 
                : '0 2px 8px rgba(59, 130, 246, 0.3)',
              backgroundColor: isDark ? '#1e3b8a' : '#3b82f6',
              '&:hover': {
                boxShadow: isDark
                  ? '0 4px 12px rgba(30, 59, 138, 0.6)'
                  : '0 4px 12px rgba(59, 130, 246, 0.4)',
                transform: 'translateY(-1px)',
                transition: 'all 0.3s ease',
                backgroundColor: isDark ? '#1e40af' : '#2563eb',
              },
            }}
          >
            {t('serviceTasks.createTask')}
          </Button>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
            }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <Paper 
          elevation={0}
          sx={{ 
            borderRadius: 3,
            border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
            overflow: 'hidden',
            boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.05)',
            bgcolor: theme.palette.background.paper,
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 12 }}>
              <CircularProgress size={48} />
            </Box>
          ) : tasks.length === 0 ? (
            <Box 
              sx={{ 
                textAlign: 'center', 
                py: 12,
                px: 4,
              }}
            >
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  px: 6,
                }}
              >
                <AddIcon sx={{ fontSize: 64, color: isDark ? '#6b7280' : '#94a3b8' }} />
              </Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mb: 1,
                }}
              >
                {t('serviceTasks.noTasks')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('serviceTasks.noTasksDescription')}
              </Typography>
              <Button
                variant="outlined"
                size="medium"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{
                  borderRadius: 2,
                  px: 2.5,
                  py: 0.75,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  borderColor: isDark ? 'rgba(59, 130, 246, 0.5)' : 'rgba(59, 130, 246, 0.5)',
                  color: isDark ? '#60a5fa' : '#3b82f6',
                  '&:hover': {
                    borderColor: isDark ? '#60a5fa' : '#2563eb',
                    bgcolor: isDark ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
                  },
                }}
              >
                {t('serviceTasks.createFirstTask')}
              </Button>
            </Box>
          ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow 
                  sx={{ 
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc',
                    '& th': {
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      fontSize: '0.875rem',
                      borderBottom: isDark ? '2px solid rgba(255, 255, 255, 0.1)' : '2px solid #e2e8f0',
                      py: 2,
                    }
                  }}
                >
                  <TableCell>{t('serviceTasks.localityType')}</TableCell>
                  <TableCell>{t('serviceTasks.locality')}</TableCell>
                  <TableCell>{t('serviceTasks.priority')}</TableCell>
                  <TableCell>{t('serviceTasks.status')}</TableCell>
                  <TableCell>{t('serviceTasks.dueOn')}</TableCell>
                  <TableCell>{t('serviceTasks.notes')}</TableCell>
                  <TableCell>{t('serviceTasks.image')}</TableCell>
                  <TableCell align="right">{t('serviceTasks.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.map((task, index) => (
                  <TableRow 
                    key={task.id}
                    sx={{
                      '&:hover': {
                        bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                        transition: 'background-color 0.2s ease',
                      },
                      '& td': {
                        borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #f1f5f9',
                        py: 2.5,
                      }
                    }}
                  >
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      <Chip 
                        label={task.localityType} 
                        size="small"
                        sx={{
                          bgcolor: isDark ? 'rgba(99, 102, 241, 0.2)' : '#e0e7ff',
                          color: isDark ? '#a5b4fc' : '#4338ca',
                          fontWeight: 600,
                          borderRadius: 1.5,
                        }}
                      />
                      {task.source === 'chat' && (
                        <Chip
                          label={t('serviceTasks.sourceChat')}
                          size="small"
                          sx={{
                            bgcolor: isDark ? 'rgba(251, 191, 36, 0.2)' : '#fef3c7',
                            color: isDark ? '#fcd34d' : '#b45309',
                            fontWeight: 600,
                            borderRadius: 1.5,
                          }}
                        />
                      )}
                    </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {task.localityType === 'Room' && task.room
                          ? `Room ${task.room.roomNumber}`
                          : task.localityId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={task.priority}
                        size="small"
                        color={getPriorityColor(task.priority) as any}
                        sx={{
                          fontWeight: 600,
                          borderRadius: 1.5,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const statusStyle = getStatusDisplay(task.status, t, isDark);
                        return (
                          <Chip
                            label={statusStyle.label}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              borderRadius: 1.5,
                              bgcolor: statusStyle.bg,
                              color: statusStyle.color,
                            }}
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {task.dueOn ? formatDate(task.dueOn) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {task.notes ? (
                        <Typography 
                          variant="body2" 
                          noWrap 
                          sx={{ 
                            maxWidth: 250,
                            color: 'text.secondary',
                          }}
                        >
                          {task.notes}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {task.imageString ? (
                        <Box sx={{ position: 'relative', width: 40, height: 40 }}>
                          <Box
                            component="img"
                            src={
                              task.imageString.startsWith('data:') || task.imageString.startsWith('http')
                                ? task.imageString
                                : `data:image/jpeg;base64,${task.imageString}`
                            }
                            alt=""
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1.5,
                              objectFit: 'cover',
                              border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0',
                              display: 'block',
                            }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              if (target) target.style.display = 'none';
                              const fallback = target.nextElementSibling as HTMLElement;
                              if (fallback) fallback.style.display = 'flex';
                            }}
                          />
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1.5,
                              bgcolor: isDark ? 'rgba(14, 165, 233, 0.2)' : '#e0f2fe',
                              display: 'none',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                            }}
                          >
                            <ImageIcon sx={{ fontSize: 20, color: isDark ? '#38bdf8' : '#0284c7' }} />
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.disabled">-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(task)}
                        sx={{
                          color: isDark ? '#60a5fa' : '#3b82f6',
                          '&:hover': {
                            bgcolor: isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                          },
                        }}
                        title={task.source === 'chat' ? t('serviceTasks.chatTaskReadOnly') : t('serviceTasks.editTask')}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {canDelete && (
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(task.id)}
                          disabled={task.source === 'chat'}
                          sx={{
                            color: isDark ? '#f87171' : '#ef4444',
                            ml: 0.5,
                            '&:hover': {
                              bgcolor: isDark ? 'rgba(248, 113, 113, 0.2)' : '#fee2e2',
                            },
                          }}
                          title={task.source === 'chat' ? t('serviceTasks.chatTaskReadOnly') : undefined}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: isDark ? '0 20px 40px rgba(0,0,0,0.5)' : '0 20px 40px rgba(0,0,0,0.15)',
            bgcolor: theme.palette.background.paper,
          }
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: '1.5rem',
            pb: 1,
            pt: 3,
            color: theme.palette.text.primary,
          }}
        >
          {editingTask ? t('serviceTasks.editTask') : t('serviceTasks.createTask')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* Department selection */}
            <FormControl fullWidth>
              <InputLabel>{t('serviceTasks.department')}</InputLabel>
              <Select
                value={selectedDepartmentId}
                onChange={(e: SelectChangeEvent<string>) => {
                  setSelectedDepartmentId(e.target.value);
                  setSelectedGroupId(''); // Reset group when department changes
                  setFormData({
                    ...formData,
                    departmentId: e.target.value,
                    taskTypeId: '',
                  });
                }}
                label={t('serviceTasks.department')}
                disabled={configLoading || !hasDepartments || isChatTaskReadOnly}
              >
                {configLoading ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} /> Loading...
                  </MenuItem>
                ) : hasDepartments ? (
                  serviceTaskConfig?.Departments
                    ?.filter((dept) => !dept.Inactive)
                    .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0))
                    .map((dept) => (
                      <MenuItem key={dept.ID} value={dept.ID}>
                        {dept.Code} - {dept.Description}
                      </MenuItem>
                    ))
                ) : (
                  <MenuItem disabled>{t('serviceTasks.noDepartments')}</MenuItem>
                )}
              </Select>
            </FormControl>

            {/* Group selection: show only after department chosen */}
            {selectedDepartmentId && (
              <FormControl fullWidth>
                <InputLabel>{t('serviceTasks.group')}</InputLabel>
                <Select
                  value={selectedGroupId}
                  onChange={(e: SelectChangeEvent<string>) => {
                    setSelectedGroupId(e.target.value);
                    setFormData({
                      ...formData,
                      taskTypeId: '', // Reset type when group changes
                    });
                  }}
                  label={t('serviceTasks.group')}
                  disabled={configLoading || !serviceTaskConfig?.Groups?.length || isChatTaskReadOnly}
                >
                  {configLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} /> Loading...
                    </MenuItem>
                  ) : serviceTaskConfig?.Groups?.length ? (
                    serviceTaskConfig.Groups
                      .filter((group) => {
                        if (!selectedDepartmentId) return true;
                        return group.Department?.ID === selectedDepartmentId && !group.Inactive;
                      })
                      .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0))
                      .map((group) => (
                        <MenuItem key={group.ID} value={group.ID}>
                          {group.Code} - {group.Description}
                        </MenuItem>
                      ))
                  ) : (
                    <MenuItem disabled>{t('serviceTasks.noGroups')}</MenuItem>
                  )}
                </Select>
              </FormControl>
            )}

            {/* Task type selection: show only after group chosen */}
            {selectedGroupId && (
              <FormControl fullWidth>
                <InputLabel>{t('serviceTasks.taskType')}</InputLabel>
                <Select
                  value={formData.taskTypeId}
                  onChange={(e: SelectChangeEvent<string>) =>
                    setFormData({ ...formData, taskTypeId: e.target.value })
                  }
                  label={t('serviceTasks.taskType')}
                  disabled={
                    configLoading ||
                    !serviceTaskConfig?.Types?.length ||
                    isChatTaskReadOnly
                  }
                >
                  {configLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} /> Loading...
                    </MenuItem>
                  ) : serviceTaskConfig?.Types?.length ? (
                    serviceTaskConfig.Types
                      .filter((type) => {
                        if (!selectedGroupId) return false;
                        return type.Group?.ID === selectedGroupId && !type.Inactive;
                      })
                      .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0))
                      .map((type) => (
                        <MenuItem key={type.ID} value={type.ID}>
                          {type.Code} - {type.Description}
                        </MenuItem>
                      ))
                  ) : (
                    <MenuItem disabled>{t('serviceTasks.noTaskTypes')}</MenuItem>
                  )}
                </Select>
              </FormControl>
            )}

            {hasDepartments && !selectedDepartmentId && (
              <Alert severity="info">
                {t('serviceTasks.selectDepartmentFirst')}
              </Alert>
            )}
            {selectedDepartmentId && !selectedGroupId && (
              <Alert severity="info">
                {t('serviceTasks.selectGroupFirst')}
              </Alert>
            )}

            <FormControl fullWidth>
              <InputLabel>{t('serviceTasks.localityType')}</InputLabel>
              <Select
                value={formData.localityType}
                onChange={(e: SelectChangeEvent<string>) => {
                  setFormData({ 
                    ...formData, 
                    localityType: e.target.value as any,
                    localityId: '', // Reset locality when type changes
                  });
                }}
                label={t('serviceTasks.localityType')}
                disabled={user?.role === 'operator' || isChatTaskReadOnly}
              >
                <MenuItem value="Room">Room</MenuItem>
                {user?.role !== 'operator' && (
                  <>
                    <MenuItem value="FunctionSpace">Function Space</MenuItem>
                    <MenuItem value="Location">Location</MenuItem>
                    <MenuItem value="PublicPlace">Public Place</MenuItem>
                  </>
                )}
              </Select>
            </FormControl>
            {user?.role === 'operator' && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {t('serviceTasks.operatorRoomOnly')}
              </Alert>
            )}

            {formData.localityType === 'Room' ? (
              <FormControl fullWidth>
                <InputLabel>{t('serviceTasks.room')}</InputLabel>
                <Select
                  value={formData.localityId}
                  onChange={(e: SelectChangeEvent<string>) =>
                    setFormData({ ...formData, localityId: e.target.value })
                  }
                  label={t('serviceTasks.room')}
                  disabled={rooms.length === 0 || isChatTaskReadOnly}
                >
                  {rooms.length === 0 ? (
                    <MenuItem disabled>
                      {t('serviceTasks.noRoomsAvailable')}
                    </MenuItem>
                  ) : (
                    rooms.map((room) => (
                      <MenuItem key={room.id} value={room.id}>
                        {room.roomNumber}
                        {room.floor && ` - Floor ${room.floor}`}
                        {room.roomType && ` (${room.roomType})`}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            ) : (
              <TextField
                label={t('serviceTasks.locality')}
                value={formData.localityId}
                onChange={(e) =>
                  setFormData({ ...formData, localityId: e.target.value })
                }
                placeholder={t('serviceTasks.enterLocality')}
                fullWidth
                disabled={isChatTaskReadOnly}
              />
            )}

            <FormControl fullWidth>
              <InputLabel>{t('serviceTasks.priority')}</InputLabel>
              <Select
                value={formData.priority}
                onChange={(e: SelectChangeEvent<string>) =>
                  setFormData({ ...formData, priority: e.target.value as any })
                }
                label={t('serviceTasks.priority')}
                disabled={isChatTaskReadOnly}
              >
                <MenuItem value="Low">Low</MenuItem>
                <MenuItem value="Normal">Normal</MenuItem>
                <MenuItem value="High">High</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label={t('serviceTasks.dueOn')}
              type="datetime-local"
              value={formData.dueOn}
              onChange={(e) =>
                setFormData({ ...formData, dueOn: e.target.value })
              }
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled={isChatTaskReadOnly}
            />

            <TextField
              label={t('serviceTasks.notes')}
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              multiline
              rows={3}
              fullWidth
              disabled={isChatTaskReadOnly}
            />

            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}>
                {t('serviceTasks.image')}
              </Typography>
              {imagePreview ? (
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Box
                    component="img"
                    src={imagePreview.startsWith('data:') || imagePreview.startsWith('http') ? imagePreview : `data:image/jpeg;base64,${imagePreview}`}
                    alt="Preview"
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 200,
                      borderRadius: 2,
                      border: isDark ? '2px solid rgba(255, 255, 255, 0.1)' : '2px solid #e2e8f0',
                    }}
                  />
                  {!isChatTaskReadOnly && (
                    <IconButton
                      size="small"
                      onClick={handleRemoveImage}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.9)',
                        color: isDark ? '#fff' : undefined,
                        '&:hover': {
                          bgcolor: isDark ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 1)',
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ) : (
                !isChatTaskReadOnly && (
                  <Button
                    variant="outlined"
                    size="medium"
                    component="label"
                    startIcon={<ImageIcon />}
                    sx={{
                      borderRadius: 2,
                      px: 2,
                      py: 0.75,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      borderColor: isDark ? 'rgba(59, 130, 246, 0.5)' : 'rgba(59, 130, 246, 0.5)',
                      color: isDark ? '#60a5fa' : '#3b82f6',
                      '&:hover': {
                        borderColor: isDark ? '#60a5fa' : '#2563eb',
                        bgcolor: isDark ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
                      },
                    }}
                  >
                    {t('serviceTasks.uploadImage')}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleImageUpload}
                    />
                  </Button>
                )
              )}
            </Box>

            {editingTask?.originId === 'whatsapp' && editingTask?.waSessionId && (
              <WhatsappTaskThread taskId={editingTask.id} />
            )}

            {editingTask &&
              (editingTask.originId === 'pms-ai' ||
                (editingTask.notes || '').includes('chat:')) && (
              <ChatTaskThread taskId={editingTask.id} />
            )}

          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCloseDialog}
            size="medium"
            sx={{
              borderRadius: 2,
              px: 2.5,
              py: 0.75,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: theme.palette.text.primary,
              '&:hover': {
                bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            size="medium"
            disabled={isChatTaskReadOnly}
            sx={{
              borderRadius: 2,
              px: 2.5,
              py: 0.75,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              boxShadow: isDark 
                ? '0 2px 8px rgba(59, 130, 246, 0.4)' 
                : '0 2px 8px rgba(59, 130, 246, 0.3)',
              background: isDark
                ? 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)'
                : 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
              '&:hover': {
                boxShadow: isDark
                  ? '0 4px 12px rgba(59, 130, 246, 0.5)'
                  : '0 4px 12px rgba(59, 130, 246, 0.4)',
                background: isDark
                  ? 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)'
                  : 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              },
            }}
          >
            {editingTask ? t('common.update') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

export default ServiceTasksPage;

